/**
 * ProCarerWelcome — Pro Carer Circle "Welcome / Start Here" page.
 *
 * A simple guided ENTRANCE to the existing Pro Carer Circle workspace —
 * deliberately not a replacement, redesign or restructuring of
 * ProCarerCircleHub.jsx. Every link below points at an existing route,
 * hub anchor or tool. No new functionality (e.g. Care Needs browsing)
 * is introduced here. No progress markers in this step.
 */
import { Link } from "react-router-dom";
import {
  Compass, PoundSterling, Search, MessagesSquare, Wrench, RefreshCcw,
  ArrowRight, ArrowUpRight,
} from "lucide-react";
import { PageHero } from "../components/shared/Primitives";

function StageLink({ to, children }) {
  const isHash = to.includes("#");
  return (
    <Link
      to={to}
      className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3.5 py-1.5 text-sm font-medium text-ink hover:border-teal hover:text-teal transition-colors"
      data-testid={`pcw-link-${to.replace(/[/#]/g, "-")}`}
    >
      {children}
      {isHash ? <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} /> : <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={2} />}
    </Link>
  );
}

const STAGES = [
  {
    n: 1,
    icon: Compass,
    title: "Prepare to work independently",
    body: "Get the essentials in place before you take on independent care work — how self-employment and IR35 work, the training, DBS and insurance most families expect, setting boundaries from day one, and your Pro Carer Profile.",
    links: [
      { label: "Self-employment guidance", to: "/wim-blog/self-employment-isnt-just-higher-pay" },
      { label: "IR35 guidance", to: "/wim-blog/ir35-explained-self-employed-carers" },
      { label: "Training & DBS guidance", to: "/workspace/pro-carer-circle/training-cpd" },
      { label: "Insurance guidance", to: "/wim-blog/insurance-every-independent-caregiver-uk" },
      { label: "Boundaries", to: "/workspace/pro-carer-circle/working-independently" },
      { label: "Your Pro Carer Profile", to: "/workspace/pro-carer-profile" },
    ],
  },
  {
    n: 2,
    icon: PoundSterling,
    title: "Price and protect the work",
    body: "Set a rate you can sustain, then put the right paperwork in place before care begins — agreements, payment protection, and a clear notice and cancellation position.",
    links: [
      { label: "Hourly Care Rate Calculator", to: "/workspace/tools/hourly-care-rate" },
      { label: "Live-In Care Rate Calculator", to: "/workspace/tools/live-in-care-rate" },
      { label: "Agreements", to: "/workspace/pro-carer-circle/contracts-agreements" },
      { label: "Payment protection", to: "/workspace/pro-carer-circle/payment-protection" },
      { label: "Notice & cancellation", to: "/workspace/pro-carer-circle/payment-protection" },
      { label: "Professional boundaries", to: "/workspace/pro-carer-circle/working-independently" },
    ],
  },
  {
    n: 3,
    icon: Search,
    title: "Find care opportunities",
    body: "Browse published Care Needs from families looking for independent care. JCC does not match or recommend carers — you decide which opportunities may be relevant to you.",
    links: [
      { label: "Browse Care Opportunities", to: "/workspace/pro-carer-circle/care-opportunities" },
      { label: "Create / manage your Pro Carer Profile", to: "/workspace/pro-carer-profile" },
    ],
  },
  {
    n: 4,
    icon: MessagesSquare,
    title: "Discuss a possible arrangement",
    body: "Once you're in contact with a family, a Care Discussion Room gives you both a private space to work through duties, rates, breaks, nights and boundaries — and to reach a Shared Agreement together.",
    links: [
      { label: "Care Discussion Rooms", to: "/care-rooms" },
      { label: "What is a Care Discussion Room?", to: "/care-discussion-room" },
    ],
  },
  {
    n: 5,
    icon: Wrench,
    title: "Manage active care work",
    body: "Once care is underway, these are your everyday tools — on-shift forms, handovers and incident records, invoicing, and staying on top of your tax admin.",
    links: [
      { label: "Shift Toolkit (forms, handovers & incident records)", to: "/workspace/pro-carer-circle/shift-kit" },
      { label: "Invoice Generator", to: "/workspace/tools/tax-confidence-kit#invoice-generator" },
      { label: "Invoice Record", to: "/workspace/tools/tax-confidence-kit#invoices" },
      { label: "Tax Confidence Kit", to: "/workspace/tools/tax-confidence-kit" },
    ],
  },
  {
    n: 6,
    icon: RefreshCcw,
    title: "Review and remain current",
    body: "Come back here whenever it's useful — check your rates still reflect your work, your invoices and tax records are up to date, your training is current, and your profile reflects where you are now.",
    links: [
      { label: "Review your rates", to: "/workspace/pro-carer-circle/pricing-income" },
      { label: "Review invoices & tax records", to: "/workspace/tools/tax-confidence-kit#invoices" },
      { label: "Training & CPD", to: "/workspace/pro-carer-circle/training-cpd" },
      { label: "Update your profile", to: "/workspace/pro-carer-profile" },
      { label: "Current JCC guidance (WIM Blog)", to: "/wim-blog" },
      { label: "Care News & Policy Watch", to: "/care-news" },
    ],
  },
];

export default function ProCarerWelcome() {
  return (
    <div data-testid="page-pro-carer-welcome">
      <PageHero
        eyebrow="Pro Carer Office"
        title="Welcome to Pro Carer Office"
        description="You do not need to use every tool immediately. Choose the stage that best describes where you are now, and JCC will guide you towards the tools and information most relevant to you."
      >
        <Link to="/workspace/pro-carer-circle" className="btn-outline text-sm" data-testid="pcw-go-to-hub">
          Go to the full Pro Carer Office workspace
        </Link>
      </PageHero>

      <section className="section pt-8 pb-20">
        <div className="container-jcc max-w-4xl space-y-6">
          {STAGES.map((s) => {
            const Icon = s.icon;
            return (
              <div key={s.n} className="card-base p-6 md:p-7" data-testid={`pcw-stage-${s.n}`}>
                <div className="flex items-start gap-4">
                  <div className="flex flex-col items-center shrink-0">
                    <div className="w-11 h-11 rounded-xl bg-teal/10 text-teal flex items-center justify-center">
                      <Icon className="w-5 h-5" strokeWidth={1.75} />
                    </div>
                    <span className="mt-1.5 text-xs font-semibold text-ink-muted">Stage {s.n}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2 className="font-display font-semibold text-lg md:text-xl text-ink">{s.title}</h2>
                    <p className="mt-2 text-sm md:text-base text-ink-muted leading-relaxed">{s.body}</p>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {s.links.map((l) => (
                        <StageLink key={l.label + l.to} to={l.to}>{l.label}</StageLink>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}
